import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { 
  Stethoscope, 
  Activity, 
  Brain,
  MessageSquare,
  Heart,
  Shield,
  Syringe,
  Users,
  Home as HomeIcon,
  Accessibility,
  ArrowRight,
  Phone,
  CheckCircle2,
  Clock
} from 'lucide-react';

const SERVICE_IMAGE_1 = "https://images.unsplash.com/photo-1765896387387-0538bc9f997e?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA4Mzl8MHwxfHNlYXJjaHwxfHxudXJzZSUyMGNhcmVnaXZlciUyMHNtaWxpbmclMjBzZW5pb3IlMjB3b21hbnxlbnwwfHx8fDE3NzE2MjI5ODd8MA&ixlib=rb-4.1.0&q=85";
const SERVICE_IMAGE_2 = "https://images.unsplash.com/photo-1765896387377-e293914d1e69?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNTl8MHwxfHNlYXJjaHwxfHxob21lJTIwY2FyZSUyMG51cnNlJTIwaGVscGluZyUyMHNlbmlvciUyMHBhdGllbnR8ZW58MHx8fHwxNzcxNjIyOTcwfDA&ixlib=rb-4.1.0&q=85";
const SERVICE_IMAGE_3 = "https://customer-assets.emergentagent.com/job_care-solutions-pro/artifacts/qwnqv4z5_pexels-shkrabaanthony-7345460.jpg";
const SERVICE_IMAGE_4 = "https://customer-assets.emergentagent.com/job_care-solutions-pro/artifacts/suhsrn8u_pexels-polina-zimmerman-3958389.jpg";
const SERVICE_IMAGE_5 = "https://customer-assets.emergentagent.com/job_care-solutions-pro/artifacts/mkqo0h5a_pexels-mikhail-nilov-8943340.jpg";
const SERVICE_IMAGE_6 = "https://customer-assets.emergentagent.com/job_care-solutions-pro/artifacts/2wdik55g_pexels-cottonbro-5721555.jpg";

const mainServices = [
  {
    icon: Stethoscope,
    title: 'Skilled Nursing',
    description: 'Comprehensive nursing care delivered by registered nurses in the comfort of your home.',
    details: [
      'Medication Management',
      'Diabetic Teaching & Care',
      'Wound Care Management',
      'Ostomy/Urological Care',
      'Post-operative Care',
      'Chronic Disease Management'
    ],
    image: SERVICE_IMAGE_1,
  },
  {
    icon: Activity,
    title: 'Physical Therapy',
    description: 'Restore mobility, strength, and function with personalized rehabilitation programs.',
    details: [
      'Post-operative Orthopedic Care',
      'Balance & Fall Prevention',
      'Stroke Rehabilitation',
      'Vestibular Therapy',
      'Aqua Therapy (Region Specific)',
      'Maintenance Therapy'
    ],
    image: SERVICE_IMAGE_2,
  },
  {
    icon: Brain,
    title: 'Occupational Therapy',
    description: 'Regain independence in daily activities and improve quality of life.',
    details: [
      'Activities of Daily Living (ADL)',
      'Home Safety Assessment',
      'Cognitive Rehabilitation',
      'Adaptive Equipment Training',
      'Fine Motor Skills',
      'Energy Conservation'
    ],
    image: SERVICE_IMAGE_3,
  },
  {
    icon: MessageSquare,
    title: 'Speech Therapy',
    description: 'Expert care for communication and swallowing disorders.',
    details: [
      'Swallowing Disorders (Dysphagia)',
      'Language Skills Recovery',
      'Cognitive-Communication',
      'Voice Disorders',
      'Aphasia Treatment',
      'Memory Strategies'
    ],
    image: SERVICE_IMAGE_4,
  },
  {
    icon: Heart,
    title: 'Cardiac & Pulmonary Care',
    description: 'Specialized management for heart and lung conditions.',
    details: [
      'CHF Management',
      'COPD Care',
      'Post-cardiac Procedure Recovery',
      'Oxygen Therapy Education',
      'Activity Tolerance Training',
      'Lifestyle Modification'
    ],
    image: SERVICE_IMAGE_5,
  },
  {
    icon: Shield,
    title: 'Wound Care Management',
    description: 'Advanced wound care including complex and surgical wounds.',
    details: [
      'Complex Wound Care',
      'Surgical Wound Management',
      'Pressure Ulcer Treatment',
      'Wound VAC Therapy',
      'Pleurex Catheter Care',
      'Lymphedema Management'
    ],
    image: SERVICE_IMAGE_6,
  },
];

const additionalServices = [
  {
    icon: Syringe,
    title: 'Infusion Therapy',
    description: 'IV therapy services administered safely in your home.',
  },
  {
    icon: Brain,
    title: 'Behavioral Health',
    description: 'Mental health support and psychiatric nursing services.',
  },
  {
    icon: Activity,
    title: 'Neurological Disorders',
    description: 'Specialized care for neurological conditions including Alzheimer\'s & Dementia.',
  },
  {
    icon: Users,
    title: 'Medical Social Worker',
    description: 'Support with resources, counseling, and care coordination.',
  },
  {
    icon: HomeIcon,
    title: 'Home Health Aide',
    description: 'Personal care assistance and support with daily activities.',
  },
  {
    icon: Accessibility,
    title: 'Lymphedema Therapy',
    description: 'Specialized treatment for lymphedema management.',
  },
];

export const Services = () => {
  return (
    <div className="min-h-screen">
      {/* Hero Section */}
      <section className="relative py-24 bg-[#000080]" data-testid="services-hero">
        <div className="absolute inset-0 bg-gradient-to-r from-[#000080] to-[#1E3A8A]"></div>
        <div className="container mx-auto px-4 md:px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="inline-block text-[#34D399] font-semibold mb-4">OUR SERVICES</span>
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-white mb-6">
              Comprehensive Home Healthcare
            </h1>
            <p className="text-xl text-white/80 mb-8">
              Our team of caring professionals provides a wide range of skilled home healthcare 
              services to help you achieve your highest level of health at home.
            </p>
            <div className="flex items-center gap-3 bg-white/10 backdrop-blur-sm rounded-full px-6 py-3 inline-flex">
              <Clock className="w-5 h-5 text-[#34D399]" />
              <span className="text-white">24/7 Clinical On-Call Support Available</span>
            </div>
          </div>
        </div>
      </section>

      {/* Main Services */}
      <section className="py-20 bg-white" data-testid="main-services">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-[#00A878] font-semibold mb-4">SKILLED SERVICES</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
              Skilled Nursing & Therapy Services
            </h2>
            <p className="text-lg text-[#334155]">
              Our skilled professionals provide expert care tailored to your individual needs, 
              helping you recover and maintain your independence at home.
            </p>
          </div>

          <div className="space-y-24">
            {mainServices.map((service, index) => (
              <div 
                key={service.title}
                className={`grid lg:grid-cols-2 gap-12 items-center ${
                  index % 2 === 1 ? 'lg:flex-row-reverse' : ''
                }`}
                data-testid={`service-detail-${index}`}
              >
                <div className={index % 2 === 1 ? 'lg:order-2' : ''}>
                  <div className="w-14 h-14 rounded-2xl bg-[#ECFDF5] flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-[#00A878]" />
                  </div>
                  <h3 className="text-2xl md:text-3xl font-bold text-[#0F172A] mb-4">
                    {service.title}
                  </h3>
                  <p className="text-lg text-[#334155] mb-6 leading-relaxed">
                    {service.description}
                  </p>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {service.details.map((detail, idx) => (
                      <div key={idx} className="flex items-center gap-2">
                        <CheckCircle2 className="w-5 h-5 text-[#00A878] flex-shrink-0" />
                        <span className="text-[#334155]">{detail}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className={index % 2 === 1 ? 'lg:order-1' : ''}>
                  <img
                    src={service.image}
                    alt={service.title}
                    className="rounded-2xl shadow-xl w-full h-[400px] object-cover"
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Additional Services */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="additional-services">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <span className="inline-block text-[#00A878] font-semibold mb-4">MORE SERVICES</span>
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
              Additional Care Services
            </h2>
            <p className="text-lg text-[#334155]">
              We offer a comprehensive range of additional services to support your complete 
              healthcare needs at home.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {additionalServices.map((service, index) => (
              <Card 
                key={service.title}
                className="service-card bg-white border border-slate-100"
                data-testid={`additional-service-${index}`}
              >
                <CardContent className="p-8">
                  <div className="w-14 h-14 rounded-2xl bg-[#000080]/10 flex items-center justify-center mb-6">
                    <service.icon className="w-7 h-7 text-[#000080]" />
                  </div>
                  <h3 className="text-xl font-semibold text-[#0F172A] mb-3">{service.title}</h3>
                  <p className="text-[#334155] leading-relaxed">{service.description}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Insurance & Coverage */}
      <section className="py-20 bg-white" data-testid="insurance-section">
        <div className="container mx-auto px-4 md:px-6 lg:px-8">
          <div className="bg-[#ECFDF5] rounded-3xl p-12 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-6">
              Medicare Certified Services
            </h2>
            <p className="text-lg text-[#334155] mb-8 max-w-2xl mx-auto">
              Senior Care Solutions is Medicare certified, which means many of our services 
              may be covered by Medicare and other insurance plans. Our team will help you 
              understand your coverage options.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <div className="bg-white rounded-full px-6 py-3 shadow-md">
                <span className="text-[#0F172A] font-semibold">Medicare</span>
              </div>
              <div className="bg-white rounded-full px-6 py-3 shadow-md">
                <span className="text-[#0F172A] font-semibold">Medicaid</span>
              </div>
              <div className="bg-white rounded-full px-6 py-3 shadow-md">
                <span className="text-[#0F172A] font-semibold">Private Insurance</span>
              </div>
              <div className="bg-white rounded-full px-6 py-3 shadow-md">
                <span className="text-[#0F172A] font-semibold">Private Pay</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-[#000080]" data-testid="services-cta">
        <div className="container mx-auto px-4 md:px-6 lg:px-8 text-center">
          <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
            Need Home Healthcare Services?
          </h2>
          <p className="text-lg text-white/80 mb-8 max-w-2xl mx-auto">
            Contact us today to discuss your care needs. Our team will work with you 
            to create a personalized care plan.
          </p>
          <div className="flex flex-col sm:flex-row justify-center gap-4">
            <Link to="/contact" data-testid="services-cta-contact">
              <Button 
                size="lg" 
                className="bg-[#00A878] hover:bg-[#065F46] text-white rounded-full px-8 py-6 text-lg font-semibold"
              >
                Request a Consultation
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <a href="tel:+15612990100" data-testid="services-cta-call">
              <Button 
                size="lg" 
                variant="outline"
                className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-[#000080] rounded-full px-8 py-6 text-lg font-semibold"
              >
                <Phone className="w-5 h-5 mr-2" />
                (561) 299-0100
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Services;
